import { Dispatch } from '@reduxjs/toolkit';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Period } from 'dashboard/lib/unixTime';
import { getPeriod } from 'dashboard/lib/utils/charting';
import { formatDateRangeEnd, formatDateRangeStart } from 'dashboard/lib/utils/formatters';
import { Environment } from 'dashboard/models/environment';
import { ExporterFileType, ExporterMetricsInquiry } from 'dashboard/models/exporter';
import {
  InquiryFilterOptions,
  MetricsInquiryMedianCompletion,
  MetricsInquiryRecentActivityVolume,
  MetricsInquiryVolume,
  MetricsTemplateFunnel,
  MetricsTemplateFunnelFlow,
  MetricsVolumeSummary,
} from 'dashboard/models/metrics';
import {
  MetricsFilterParams,
  setMetricInquiryFilterOptions,
  setMetricsInquiryGranularities,
  setMetricsInquiryTemplateVersionIdLoaded,
  setMetricsMedianCompletionVolume,
  setMetricsTemplateFunnel,
  setMetricsTemplateFunnelConversionOverTime,
  setMetricsTemplateFunnelFlow,
  setMetricsTemplateIdLoaded,
  setMetricsTemplateVolumeSummary,
  setMetricsVolume,
  setMetricsVolumeOverTime,
  setRecentActivityVolume,
} from 'dashboard/redux/actions/metrics-inquiry';
import { InquiryGranularities } from 'dashboard/redux/reducers/metrics-inquiry';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export interface TemplateFunnelFlowResponse {
  meta: MetricsTemplateFunnelFlow[];
  updated_at: Iso8601DateString;
}

export interface VolumeSummaryResponse {
  meta: MetricsInquiryVolume;
  filter_options: InquiryFilterOptions;
  delta: {
    volume: MetricsInquiryVolume;
  };
}

export interface MedianCompletionVolumeResponse {
  meta: MetricsInquiryMedianCompletion;
  delta: {
    completion: MetricsInquiryMedianCompletion;
  };
}

export interface RecentActivityVolumeResponse {
  meta: MetricsInquiryRecentActivityVolume;
  delta: MetricsInquiryRecentActivityVolume;
}

function createParamsForList(
  currentEnvironment: Environment,
  inquiryFilters: MetricsFilterParams,
  inquirySortParams: SortParams,
  fileType: ExporterFileType,
) {
  const {
    createdAtStart,
    createdAtEnd,
    templateOrInquiryTemplateIds,
    platforms,
    deviceGroups,
    countryCodes,
    groupBy,
    inquiryTagIds,
  } = inquiryFilters;
  const { sort, shouldSortDescending } = inquirySortParams;

  return {
    'file-type': fileType,
    filter: {
      'environment-id': currentEnvironment.id,
      'template-ids': templateOrInquiryTemplateIds,
      'group-by': groupBy,
      'country-codes': countryCodes,
      'device-groups': deviceGroups,
      platforms: platforms,
      'inquiry-tag-tokens': inquiryTagIds,
      'created-at-start': createdAtStart && createdAtStart.toString(),
      'created-at-end': createdAtEnd && createdAtEnd.toString(),
    },
    sort: `${shouldSortDescending ? '-' : ''}${sort}`,
  };
}

export const loadExporterMetricsInquiry =
  ({
    currentEnvironment,
    filters,
    sortParams,
    fileType,
  }: {
    currentEnvironment: Environment;
    filters: MetricsFilterParams;
    sortParams: SortParams;
    fileType: ExporterFileType;
  }) =>
  async () => {
    const params = createParamsForList(currentEnvironment, filters, sortParams, fileType);

    await SessionClientDashboard.postResourceCreate<ExporterMetricsInquiry>({
      type: ApiModel.ExporterMetricsInquiry,
      attributes: {
        ...params,
      },
    });
  };

export function loadMetricsInquiryVolume({
  currentEnvironment,
  filters,
  includeDeltas,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
  includeDeltas: boolean;
}) {
  return async (dispatch: Dispatch) => {
    const {
      createdAtStart,
      createdAtEnd,
      templateOrInquiryTemplateIds,
      groupBy,
      inquiryTagIds,
      countryCodes,
      deviceGroups,
      platforms,
    } = filters;

    const filterParams = {
      'environment-id': currentEnvironment.id,
      'template-ids': templateOrInquiryTemplateIds,
      'created-at-start': formatDateRangeStart(createdAtStart),
      'created-at-end': formatDateRangeEnd(createdAtEnd),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      period: getPeriod(createdAtStart, createdAtEnd),
      'group-by': groupBy,
      'inquiry-tag-tokens': inquiryTagIds,
      'country-codes': countryCodes,
      'device-groups': deviceGroups,
      platforms: platforms,
      'include-deltas': includeDeltas,
    };

    const volumeResponse = await SessionClientDashboard.getEndpoint<VolumeSummaryResponse>(
      ApiEndpoint.MetricsInquiriesVolume,
      {
        filter: filterParams,
      },
    );
    const volumeSummary = volumeResponse.meta;
    const filterOptions = volumeResponse.filter_options;
    const volumeDelta = volumeResponse.delta.volume;

    dispatch(setMetricsVolume(volumeSummary, new Date().toISOString(), filterOptions, volumeDelta));
  };
}

export function loadMetricsInquiryMedianCompletion({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const {
      createdAtStart,
      createdAtEnd,
      templateOrInquiryTemplateIds,
      groupBy,
      inquiryTagIds,
      countryCodes,
      deviceGroups,
      platforms,
    } = filters;

    const filterParams = {
      'environment-id': currentEnvironment.id,
      'template-ids': templateOrInquiryTemplateIds,
      'created-at-start': formatDateRangeStart(createdAtStart),
      'created-at-end': formatDateRangeEnd(createdAtEnd),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      period: getPeriod(createdAtStart, createdAtEnd),
      'group-by': groupBy,
      'inquiry-tag-tokens': inquiryTagIds,
      'country-codes': countryCodes,
      'device-groups': deviceGroups,
      platforms: platforms,
    };

    const response = await SessionClientDashboard.getEndpoint<MedianCompletionVolumeResponse>(
      ApiEndpoint.MetricsInquiriesMedianCompletionVolume,
      {
        filter: filterParams,
      },
    );
    const completionSummary = response.meta;
    const completionDelta = response.delta.completion;

    dispatch(
      setMetricsMedianCompletionVolume(
        new Date().toISOString(),
        completionDelta,
        completionSummary,
      ),
    );
  };
}

export function loadMetricsInquiryVolumeOverTime({
  currentEnvironment,
  filters,
  overviewPeriod,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
  overviewPeriod: Period;
}) {
  return async (dispatch: Dispatch) => {
    const {
      createdAtStart,
      createdAtEnd,
      templateOrInquiryTemplateIds,
      groupBy,
      inquiryTagIds,
      countryCodes,
      deviceGroups,
      platforms,
    } = filters;

    const filterParams = {
      'environment-id': currentEnvironment.id,
      'template-ids': templateOrInquiryTemplateIds,
      'created-at-start': formatDateRangeStart(createdAtStart),
      'created-at-end': formatDateRangeEnd(createdAtEnd),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      period: getPeriod(createdAtStart, createdAtEnd),
      'group-by': groupBy,
      'inquiry-tag-tokens': inquiryTagIds,
      'country-codes': countryCodes,
      'device-groups': deviceGroups,
      platforms: platforms,
      'overview-period': overviewPeriod,
    };
    const inquiryStatusOverTime = (
      await SessionClientDashboard.getResourceMetaTyped<ApiEndpoint.MetricsInquiriesVolumeOverTime>(
        ApiEndpoint.MetricsInquiriesVolumeOverTime,
        {
          filter: filterParams,
        },
      )
    ).meta;

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setMetricsVolumeOverTime(inquiryStatusOverTime, new Date().toISOString()));
  };
}

export function loadMetricsInquiryRecentActivityVolume({
  currentEnvironment,
  filters: { createdAtStart, createdAtEnd },
}: {
  currentEnvironment: Environment;
  filters: Pick<MetricsFilterParams, 'createdAtStart' | 'createdAtEnd'>;
}) {
  return async (dispatch: Dispatch) => {
    const filterParams = {
      'environment-id': currentEnvironment.id,
      'created-at-start': formatDateRangeStart(createdAtStart),
      'created-at-end': formatDateRangeEnd(createdAtEnd),
    };

    const response = await SessionClientDashboard.getEndpoint<RecentActivityVolumeResponse>(
      ApiEndpoint.MetricsInquiriesRecentActivity,
      {
        filter: filterParams,
      },
    );

    dispatch(setRecentActivityVolume(response.meta, response.delta));
  };
}

export function loadMetricsTemplateVolumeSummary({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateIds, groupBy } = filters;

    await Promise.all(
      templateIds.map(async (templateId) => {
        const { meta } = await SessionClientDashboard.getResourceMeta<MetricsVolumeSummary>(
          ApiEndpoint.MetricsInquiries,
          'summary',
          {
            filter: {
              'environment-id': currentEnvironment.id,
              'template-ids': [templateId],
              'created-at-start': formatDateRangeStart(createdAtStart),
              'created-at-end': formatDateRangeEnd(createdAtEnd),
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              period: getPeriod(createdAtStart, createdAtEnd),
              'group-by': groupBy,
            },
          },
        );

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        dispatch(setMetricsTemplateVolumeSummary(templateId, meta, new Date().toISOString()));
      }),
    );
  };
}

export function setFunnelTemplateIdLoaded({ templateId }: { templateId: string }) {
  return async (dispatch: Dispatch) => {
    dispatch(setMetricsTemplateIdLoaded(templateId));
  };
}

export function setGranularities({ granularities }: { granularities: InquiryGranularities }) {
  return async (dispatch: Dispatch) => {
    dispatch(setMetricsInquiryGranularities(granularities));
  };
}

export function setFunnelInquiryTemplateVersionIdLoaded({
  inquiryTemplateVersionId,
}: {
  inquiryTemplateVersionId: string;
}) {
  return async (dispatch: Dispatch) => {
    dispatch(setMetricsInquiryTemplateVersionIdLoaded(inquiryTemplateVersionId));
  };
}

export function loadMetricsTemplateFunnel({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateIds, groupBy } = filters;

    await Promise.all(
      templateIds.map(async (templateId) => {
        const { meta } = await SessionClientDashboard.getResourceMeta<MetricsTemplateFunnel>(
          ApiEndpoint.MetricsInquiries,
          'funnel',
          {
            filter: {
              'environment-id': currentEnvironment.id,
              'template-id': templateId,
              'created-at-start': formatDateRangeStart(createdAtStart),
              'created-at-end': formatDateRangeEnd(createdAtEnd),
              'group-by': groupBy,
            },
          },
        );

        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        dispatch(setMetricsTemplateFunnel(templateId, meta, new Date().toISOString()));
      }),
    );
  };
}

export function loadMetricsTemplateFunnelFlow({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateOrInquiryTemplateId } = filters;
    const response = await SessionClientDashboard.getEndpoint<TemplateFunnelFlowResponse>(
      ApiEndpoint.MetricsInquiriesFunnelFlow,
      {
        filter: {
          'environment-id': currentEnvironment.id,
          'template-id': templateOrInquiryTemplateId,
          'created-at-start': formatDateRangeStart(createdAtStart),
          'created-at-end': formatDateRangeEnd(createdAtEnd),
        },
      },
    );

    dispatch(
      setMetricsTemplateFunnelFlow(
        response.meta,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        templateOrInquiryTemplateId,
        new Date().toISOString(),
        response.updated_at,
      ),
    );
  };
}

export function loadMetricsTemplateFunnelConversionOverTime({
  currentEnvironment,
  filters,
  funnelPeriod,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
  funnelPeriod: Period;
}) {
  return async (dispatch: Dispatch) => {
    const {
      createdAtStart,
      createdAtEnd,
      templateOrInquiryTemplateId,
      funnelStepIds,
      inquiryTemplateVersionId,
    } = filters;

    const { meta } =
      await SessionClientDashboard.getResourceMetaTyped<ApiEndpoint.MetricsInquiriesFunnelConversionOverTime>(
        ApiEndpoint.MetricsInquiriesFunnelConversionOverTime,
        {
          filter: {
            'environment-id': currentEnvironment.id,
            'template-id': templateOrInquiryTemplateId,
            'created-at-start': formatDateRangeStart(createdAtStart),
            'created-at-end': formatDateRangeEnd(createdAtEnd),
            'funnel-step-ids': funnelStepIds,
            'inquiry-template-version-id': inquiryTemplateVersionId,
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            period: getPeriod(createdAtStart, createdAtEnd),
            'funnel-period': funnelPeriod,
          },
        },
      );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setMetricsTemplateFunnelConversionOverTime(meta, new Date().toISOString()));
  };
}

export function loadMetricsInquiryDynamicFilters({
  currentEnvironment,
  filters,
}: {
  currentEnvironment: Environment;
  filters: MetricsFilterParams;
}) {
  return async (dispatch: Dispatch) => {
    const { createdAtStart, createdAtEnd, templateOrInquiryTemplateIds, groupBy } = filters;

    const filterParams = {
      'environment-id': currentEnvironment.id,
      'template-ids': templateOrInquiryTemplateIds,
      'created-at-start': formatDateRangeStart(createdAtStart),
      'created-at-end': formatDateRangeEnd(createdAtEnd),
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      period: getPeriod(createdAtStart, createdAtEnd),
      'group-by': groupBy,
    };

    const volumeResponse = await SessionClientDashboard.getEndpoint<VolumeSummaryResponse>(
      ApiEndpoint.MetricsInquiriesVolume,
      {
        filter: filterParams,
      },
    );
    dispatch(setMetricInquiryFilterOptions(volumeResponse.filter_options));
  };
}
